import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FitImage from "@ucs/Components/Common/FitImage";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { FC } from "react";
import styled, { css } from "styled-components";

interface OwnProps {
    image: string;
    projectLink: string;
    categoryName: string;
    projectName: string;
    productLinks: ((Readonly<PageLinkModel> | {
        url: string | undefined;
        title: string | undefined;
    } | undefined) & {
        openInNewWindow: boolean;
        overriddenTitle: string;
    })[];
    extendedStyles?: ProjectTileStyles;
}


export interface ProjectTileStyles {
    wrapper?: InjectableCss;
    image?: InjectableCss;
    content?: InjectableCss;
    categoryLabel?: ClickableProps;
    projectTitle?: TypographyProps;
    productsContainer?: InjectableCss;
    container?: InjectableCss;
}

export const projectTileStyles: ProjectTileStyles = {
    wrapper: {
        css: css`
            width: 100%;
            max-width: 360px;
            display: flex;
            flex-direction: column;
            box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.05);
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        max-width: 100%;
                    `,
                    css`
                        max-width: 100%;
                    `,
                ]);
            }}
        `,
    },
    image: {
        css: css`
            cursor: pointer;
        `,
    },
    content: {
        css: css`
            width: 100%;
            height: 168px;
            display: flex;
            flex-direction: column;
            padding: 20px 0 8px 20px;
            background-color: ${UCSColors.gray10};
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        height: 144px;
                        padding: 16px 0 6px 15px;
                    `,
                ]);
            }}
        `,
    },
    categoryLabel: {
        css: css`
            font-size: 13px;
            color: ${UCSColors.gray04};
            text-transform: uppercase;
            font-weight: 500;
            letter-spacing: 0.02em;
            margin-right: 20px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin-right: 15px;
                    `,
                ]);
            }}
        `,
    },
    projectTitle: {
        variant: "h4",
        color: UCSColors.gray00,
        css: css`
            flex: 1;
            line-height: 32px;
            margin: 4px 20px 0 0;
            max-height: 64px;
            overflow: hidden;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 17px;
                        line-height: 24px;
                        margin: 4px 15px 12px 0;
                        max-height: 210px;
                    `,
                    css``,
                ]);
            }}
        `,
    },
    productsContainer: {
        css: css`
            width: 100%;
            display: flex;
            flex-wrap: nowrap;
            overflow-x: scroll;
            gap: 4px;
            margin-top: auto;
            padding-bottom: 4px;

            ::-webkit-scrollbar-track {
                background: ${UCSColors.gray10};
            }
            ::-webkit-scrollbar-thumb {
                background: ${UCSColors.gray08};
            }
            ::-webkit-scrollbar-thumb:hover {
                background: ${UCSColors.gray07};
            }
        `,
    },
    container: {
        css: css`
            flex: 1;
            width: 100%;
            padding-top: 74%; 
            position: relative;
            overflow: hidden;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                         padding-top: 28%;
                    `,
                    css`
                        padding-top: 47%; 
                    `,
                ]);
            }}
        `,
    },
};

const ProjectTag = styled.div`
    padding: 7px 6px;
    border: 1px solid ${UCSColors.gray07};
    border-radius: 2px;
    font-size: 13px; 
    color: ${UCSColors.gray03};
    cursor: pointer;
    width: fit-content;
    white-space: nowrap;
`;

const ProjectTileCard: FC<OwnProps> = props => {
    const { image, projectLink, projectName, categoryName, productLinks, extendedStyles } = props;
    const [styles] = React.useState(() => mergeToNew(projectTileStyles, extendedStyles));

    const navigateToProject = () => {
        window.location.href = projectLink;
    }
        
    return (
        <StyledWrapper {...styles.wrapper}>
            <FitImage image={image} imageClickHandler={navigateToProject} focalPoint="center" containerStyles={styles.container}/>
            <StyledWrapper {...styles.content}>
                <Clickable {...styles.categoryLabel} href={projectLink}>{categoryName}</Clickable>
                <Clickable  href={projectLink}>
                    <Typography {...styles.projectTitle}>{projectName}</Typography>
                </Clickable>
                <StyledWrapper {...styles.productsContainer}>
                    {productLinks.map((link, idx) => 
                        <>
                            {link.url && 
                                // eslint-disable-next-line react/no-array-index-key
                                <ProjectTag key={idx}>
                                    <Clickable href={link.url}>{link.overriddenTitle || link.title}</Clickable>
                                </ProjectTag>
                            }
                        </>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default ProjectTileCard;
